import React, {createContext, useCallback, useContext, useEffect, useState} from "react";

export interface IAddToPackageContext {
    addedIds: number[];
    addIds: (ids: number[]) => void;
}

interface AddToPackageProviderProps {
    children: React.ReactNode;
    packageGenerationMessage?: string;
    packageOperationInProgress?: boolean;
    packageItemIds?: number[];
}

const AddToPackageContext =
    createContext<IAddToPackageContext | undefined>(undefined);

export const AddToPackageProvider: React.FC<
    AddToPackageProviderProps
> = ({
    children,
    packageGenerationMessage,
    packageOperationInProgress,
    packageItemIds,
}) => {
    const [addedIds, setAddedIds] = useState<number[]>([]);

    useEffect(() => {
        const generationCompleted = packageGenerationMessage === "packageGeneratingMessage" && !packageOperationInProgress;

        if (generationCompleted) {
            setAddedIds([]);
            return;
        }

        setAddedIds(packageItemIds ?? []);
    }, [packageGenerationMessage, packageOperationInProgress, packageItemIds]);

    const addIds = useCallback((ids: number[]) => {
        setAddedIds(previousIds => {
            const mergedIds = new Set(previousIds);

            ids.forEach(id => {
                if (typeof id === "number" && !Number.isNaN(id)) {
                    mergedIds.add(id);
                }
            });

            return Array.from(mergedIds);
        });
    }, []);

    return (
        <AddToPackageContext.Provider value={{ addedIds, addIds }}>
            {children}
        </AddToPackageContext.Provider>
    );
};

export const useAddToPackageContext = (): IAddToPackageContext => {
    const context = useContext(AddToPackageContext);

    if (!context) {
        throw new Error("useAddToPackageContext must be used within AddToPackageProvider");
    }

    return context;
};